<template>
  <SectionSummary>
    <SectionSummaryRow single-column>
      <SectionSummaryText :text="summary.displayName" bold />
    </SectionSummaryRow>
    <SectionSummaryRow v-if="summary.baseSubtotal.enabled">
      <template #description>
        <SectionSummaryText
          :text="summary.baseSubtotal.displayName || ''"
          bold
          aria-hidden="true"
        />
      </template>
      <template #value>
        <span class="sr-only">{{ getBaseSubtotalAriaLabelComputed }}</span>
        <SectionSummaryText aria-hidden="true" :text="baseSubtotalText" bold align-right />
      </template>
    </SectionSummaryRow>
    <template v-if="summary.taxBreakdown.enabled">
      <SectionSummaryRow>
        <template #description>
          <SectionSummaryText :text="summary.taxBreakdown.displayName || ''" aria-hidden="true" />
        </template>
        <template #value>
          <span class="sr-only">{{ getTaxBreakdownTotalAriaLabelComputed }}</span>
          <SectionSummaryText aria-hidden="true" :text="taxBreakdownTotalText" align-right />
        </template>
      </SectionSummaryRow>
      <SectionSummaryRow v-for="taxRate in visibleTaxBreakdownRates" :key="taxRate.rate">
        <template #description>
          <SectionSummaryText :text="taxRate.displayName || ''" small indent aria-hidden="true" />
        </template>
        <template #value>
          <span class="sr-only">{{
            getTaxRateAriaLabel(
              taxRate,
              pricingTable.currencySettings?.formatOptions?.currency || ''
            )
          }}</span>
          <SectionSummaryText
            aria-hidden="true"
            :text="getCurrencyFormattedValue(taxRate.value || '0')"
            align-right
          />
        </template>
      </SectionSummaryRow>
    </template>
    <SectionSummaryDivider v-if="summary.taxBreakdown.enabled" />
    <SectionSummaryRow v-if="summary.combinedSubtotal.enabled">
      <template #description>
        <SectionSummaryText
          :text="summary.combinedSubtotal.displayName || ''"
          bold
          aria-hidden="true"
        />
      </template>
      <template #value>
        <span class="sr-only">{{ getCombinedSubtotalAriaLabelComputed }}</span>
        <SectionSummaryText aria-hidden="true" :text="combinedSubTotalText" bold align-right />
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="summary.discount.enabled">
      <template #description>
        <SectionSummaryText :text="summary.discount.displayName || ''" aria-hidden="true" />
      </template>
      <template #value>
        <span class="sr-only">{{ getTotalDiscountAriaLabelComputed }}</span>
        <SectionSummaryText aria-hidden="true" :text="totalDiscountText" red align-right />
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="summary.tax.enabled">
      <template #description>
        <SectionSummaryText :text="summary.tax.displayName || ''" aria-hidden="true" />
      </template>
      <template #value>
        <span class="sr-only">{{ getTotalTaxAriaLabelComputed }}</span>
        <SectionSummaryText aria-hidden="true" :text="totalTaxText" align-right />
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow>
      <template #description>
        <SectionSummaryText :text="summary.price.displayName || ''" bold aria-hidden="true" />
      </template>
      <template #value>
        <span class="sr-only">{{ getTotalPriceAriaLabelComputed }}</span>
        <SectionSummaryText aria-hidden="true" :text="totalPriceText" bold align-right />
      </template>
    </SectionSummaryRow>
  </SectionSummary>
</template>
<script lang="ts" setup>
import { usePricingTableSummary, type Field, type PricingTable } from '@getaccept/editor-lib';
import { toRefs, computed } from 'vue';
import SectionSummary from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummary.vue';
import SectionSummaryRow from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryRow.vue';
import SectionSummaryText from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryText.vue';
import SectionSummaryDivider from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryDivider.vue';
import {
  getTaxRateAriaLabel,
  createBaseSubtotalAriaLabel,
  createTaxBreakdownTotalAriaLabel,
  createCombinedSubtotalAriaLabel,
  createTotalDiscountAriaLabel,
  createTotalTaxAriaLabel,
  createTotalPriceAriaLabel,
} from '@getaccept/editor-lib/src/helpers/pricing-table/pricing-table-accessibility.helper';

const props = defineProps<{
  pricingTable: PricingTable;
  fields: Field[];
}>();

const { pricingTable, fields } = toRefs(props);

const {
  summary,
  totalTaxText,
  totalDiscountText,
  totalPriceText,
  baseSubtotalText,
  combinedSubTotalText,
  taxBreakdownTotalText,
  getCurrencyFormattedValue,
  visibleTaxBreakdownRates,
} = usePricingTableSummary(pricingTable, fields);

const getBaseSubtotalAriaLabelComputed = computed(() =>
  createBaseSubtotalAriaLabel(summary.value, pricingTable.value)
);

const getTaxBreakdownTotalAriaLabelComputed = computed(() =>
  createTaxBreakdownTotalAriaLabel(summary.value, pricingTable.value)
);

const getCombinedSubtotalAriaLabelComputed = computed(() =>
  createCombinedSubtotalAriaLabel(summary.value, pricingTable.value)
);

const getTotalDiscountAriaLabelComputed = computed(() =>
  createTotalDiscountAriaLabel(summary.value, pricingTable.value)
);

const getTotalTaxAriaLabelComputed = computed(() =>
  createTotalTaxAriaLabel(summary.value, pricingTable.value)
);

const getTotalPriceAriaLabelComputed = computed(() =>
  createTotalPriceAriaLabel(summary.value, pricingTable.value)
);
</script>
